import { booleanAttribute } from "#ce/utils/props";
// We had bugs in the past, where the props were mutatated. Apply Object.freeze() to prevent this.
export const props = Object.freeze({
  /**
   * Can be an array of objects or strings.
   *
   * By default objects should have title and value properties.
   * Keys to use for these can be changed with the item-title, item-value, and item-props props.
   * If strings are used, they will be used for both title and value.
   *
   * @type {Array<String|Object>}
   * @default []
   */
  items: {
    type: Array,
    default: () => [],
  },
  /**
   * Property on supplied items that contains its title.
   *
   * @type {String}
   * @default "title"
   */
  itemTitle: {
    type: String,
    default: "title",
  },
  /**
   * Property on supplied items that contains its subtitle.
   *
   * @type {String}
   * @default undefined
   */
  itemSubtitle: {
    type: String,
    default: "subtitle",
  },
  /**
   * Property on supplied items that contains its read-only tag/chip labels
   * (an array of strings) rendered beneath the title.
   *
   * @type {String}
   * @default "tags"
   */
  itemTags: {
    type: String,
    default: "tags",
  },
  /**
   * Property on supplied items that contains its value.
   *
   * @type {String}
   * @default "value"
   */
  itemValue: {
    type: String,
    default: "value",
  },
  /**
   * Props object that will be applied to each item component.
   * A `true` value will treat the original object as raw props and pass it directly to the component.
   *
   * @type {String|Boolean}
   * @default "props"
   */
  itemProps: {
    type: [String, Boolean],
    default: "props",
  },
  /**
   * An array containing the values of currently selected items.
   *
   * @type {Array}
   * @default undefined
   */
  selected: {
    type: Array,
    default: undefined,
  },
  /**
   * Icon to prepend to the selected item.
   * This will be rendered before the selected item's title.
   *
   * @type {String}
   * @default undefined
   */
  selectedPrependIcon: {
    type: String,
    default: undefined,
  },
  selectedPrependIconFillColor: {
    type: String,
    default: undefined,
  },
  /**
   * Changes the selection behavior to allow multiple selections.
   *
   * @type {Boolean|String}
   * @default false
   */
  multiple: booleanAttribute(),
  /**
   * Draw a checkbox in front of every row, ticked while the row is selected, so a `multiple` list
   * shows its whole selection at a glance.
   *
   * @type {Boolean|String}
   * @default false
   */
  checkboxes: booleanAttribute(),
  /**
   * Enforces a required selection: once an item is selected it cannot be
   * toggled off by re-selecting it, so the list always keeps at least one
   * selected value. For multiple selection this prevents removing the last
   * remaining item.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Renders the list with the provided subheader on top.
   *
   * @type {String}
   * @default undefined
   */
  subheader: {
    type: String,
    default: undefined,
  },
  /**
   * The strategy to listen for key events.
   * Per default, the list only listens for key events when it is focused.
   * If the list is part of a host component that manages focus, you can set this prop to `true`.
   *
   * - `focus`: Only listen for key events while focus is inside the list. Navigation moves focus
   *   onto a row, so that row counts as inside.
   * - `true`: Listen for key events all the time.
   * - `false`: Never listen for key events.
   *
   * @default "focus"
   * @type {Boolean|String}
   */
  listenOnKeyEvent: booleanAttribute("focus"),
  /**
   * If true, the list item will not be selectable.
   * This is useful when the list is used as a menu.
   *
   * @default false
   */
  readonly: booleanAttribute(),
  /**
   * If true, list item text will wrap to multiple lines when it overflows
   * the available width, instead of being constrained to a single line.
   *
   * @type {Boolean|String}
   * @default false
   */
  wrap: booleanAttribute(),
  /**
   * Arrow Up on the first navigable row emits `exit-start` and forgets the
   * roving position instead of wrapping to the last row; the next Arrow Up
   * wraps as usual. For combobox hosts whose keyboard contract has a
   * no-highlight state between the first row and the wrap (the events
   * omnisearch).
   *
   * @type {Boolean|String}
   * @default false
   */
  exitStart: booleanAttribute(),
  /**
   * When true, scroll the currently-selected (active) row into view once on
   * mount. Off by default so inline (non-dropdown) lists never scroll their
   * page on load; dropdown/menu consumers (e.g. the quantum-select panel) opt
   * in so a pre-selected row — including one revealed by auto-expanding its
   * accordion branch — is visible when the menu opens.
   *
   * @type {Boolean|String}
   * @default false
   */
  scrollSelectedIntoView: booleanAttribute(),
  /**
   * Accessible name of the list. Set it as `aria-label` on the element; a host `aria-label` alone
   * names nothing, because the element carrying the role lives in the shadow root. A list inside a
   * picker takes the name its activator or field already carries.
   *
   * An `aria-labelledby` is not offered: the id would have to resolve inside this shadow root,
   * where a consumer's label does not live. A list with a `subheader` and no `aria-label` is named
   * by that instead.
   *
   * @type {String}
   * @default undefined
   */
  ariaLabel: {
    type: String,
    default: undefined,
  },
  /**
   * Renders the list as a tree rather than a listbox: `role="tree"`, rows as `treeitem`s, and
   * `aria-expanded` on a row that has children. For a branch list whose rows carry a disclosure
   * toggle — the accordion picker — where a listbox cannot describe the expanded state at all.
   *
   * The accordion splices a parent's children in as its siblings, so depth is stated with
   * `aria-level` on each row rather than by nesting; `aria-posinset` and `aria-setsize` are counted
   * from those levels.
   *
   * @type {Boolean|String}
   * @default false
   */
  tree: booleanAttribute(),
});
